import React, {useCallback, useEffect, useState} from 'react';
import {Box, Card, DialogActions, DialogContent, DialogTitle, Grid, Typography} from '@mui/material';
import Dialog from "../../../../../../../../components/base/dialog";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import Button from "../../../../../../../../components/base/button/button";

export const RESOLUTION_CHOICES = {
    KEEP_FORMULA: 'keep_formula',
    KEEP_PRICE: 'keep_price',
};

const FormulaDiscrepancyResolutionDialog = ({open, onClose, discrepancies, onSubmit, loading}) => {
    const [resolutions, setResolutions] = useState({});

    /**
     * Reset resolutions when dialog opens/closes.
     */
    useEffect(() => {
        if (open) {
            setResolutions({
                ...discrepancies.reduce((acc, d) => {
                    acc[d.priceLevel.id] = RESOLUTION_CHOICES.KEEP_PRICE;
                    return acc;
                }, {})
            });
        }
    }, [open, discrepancies]);

    /**
     * Handle toggle between keeping formula vs keeping price for a given price level.
     */
    const handleToggle = useCallback((priceLevelId, choice) => {
        if (!choice) return;
        setResolutions(prev => ({
            ...prev,
            [priceLevelId]: choice
        }));
    }, []);

    const allResolved = React.useMemo(() => discrepancies.length > 0 && discrepancies.every(d => !!resolutions[d.priceLevel.id]), [discrepancies, resolutions]);

    /**
     * Submit resolutions to parent component.
     */
    const handleSubmit = useCallback(() => {
        onSubmit(resolutions);
    }, [onSubmit, resolutions]);

    return (
        <Dialog
            open={open}
            title={'Resolve Formula Discrepancies'}
            setOpen={onClose}
            loading={loading}
            maxWidth={'md'}
            fullWidth
            className={'bound-dialog'}
        >
            <DialogTitle>
                <Box component='span' sx={{padding: 2, display: 'block', width: '100%'}}>
                    Resolve Formula Discrepancies
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, padding: 2}}>
                    <Typography variant={'body2'} sx={{mb: 1}}>
                        We detected that external factors (like Cost) or manual price updates have caused your live prices to drift from their formulas. Please resolve the conflicts below by selecting
                        which value to keep.
                    </Typography>

                    {discrepancies.map((d) => {
                        const isFormulaSelected = resolutions[d.priceLevel.id] === RESOLUTION_CHOICES.KEEP_FORMULA;
                        const isPriceSelected = resolutions[d.priceLevel.id] === RESOLUTION_CHOICES.KEEP_PRICE;
                        const noneSelected = !isFormulaSelected && !isPriceSelected;

                        return (
                            <Card key={d.priceLevel.id} variant={'outlined'}>
                                {/* Header */}
                                <Box sx={{p: 1.5, bgcolor: 'background.default', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <Typography variant={'body2'} fontWeight={600}>
                                        {d.priceLevel.title}
                                    </Typography>
                                </Box>
                                <Grid container spacing={0}>
                                    <Grid item xs={6} onClick={() => handleToggle(d.priceLevel.id, RESOLUTION_CHOICES.KEEP_FORMULA)}>
                                        <Card variant={'outlined'} sx={{
                                            p: 2, cursor: 'pointer',
                                            bgcolor: noneSelected ? 'background.paper' : isFormulaSelected ? 'background.paper' : 'action.disabledBackground',
                                            borderColor: noneSelected ? 'transparent' : isFormulaSelected ? 'primary.main' : 'transparent',
                                            borderWidth: 2,
                                            borderStartStartRadius: 0,
                                            borderStartEndRadius: 0,
                                            borderEndEndRadius: 0,
                                            color: isFormulaSelected ? 'text.primary' : 'text.secondary',
                                            transition: theme => theme.transitions.create(['background-color', 'color'], {
                                                duration: theme.transitions.duration.short,
                                            }),
                                        }}
                                        >
                                            <Typography variant={'caption'} fontWeight={600} display="block" gutterBottom color={isFormulaSelected ? 'primary.main' : 'inherit'}>
                                                KEEP FORMULA
                                            </Typography>
                                            <Typography variant={'h6'} fontWeight={700} sx={{my: 0.5, color: isFormulaSelected ? 'primary.main' : 'inherit'}}>
                                                <Typography
                                                    variant={'body2'} fontWeight={600} component={'span'}
                                                    sx={{opacity: 0.8, borderRadius: 0.5, padding: 0.5, backgroundColor: 'action.selected', mr: 0.5}}
                                                >
                                                    {d.formula}:
                                                </Typography> {formatMoney(d.resolvedValue)}
                                            </Typography>
                                            <Typography variant={'caption'} sx={{opacity: 0.8, display: 'block', mt: 1}}>
                                                Update the live price to match the evaluated formula.
                                            </Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} onClick={() => handleToggle(d.priceLevel.id, RESOLUTION_CHOICES.KEEP_PRICE)}>
                                        <Card variant={'outlined'} sx={{
                                            p: 2, cursor: 'pointer',
                                            bgcolor: noneSelected ? 'background.paper' : isPriceSelected ? 'background.paper' : 'action.disabledBackground',
                                            borderColor: noneSelected ? 'transparent' : isPriceSelected ? 'primary.main' : 'transparent',
                                            borderWidth: 2,
                                            borderStartStartRadius: 0,
                                            borderStartEndRadius: 0,
                                            borderEndStartRadius: 0,
                                            color: isPriceSelected ? 'text.primary' : 'text.secondary',
                                            transition: theme => theme.transitions.create(['background-color', 'color'], {
                                                duration: theme.transitions.duration.short,
                                            }),
                                        }}
                                        >
                                            <Typography variant={'caption'} fontWeight={600} display="block" gutterBottom color={isPriceSelected ? 'primary.main' : 'inherit'}>
                                                KEEP LIVE PRICE
                                            </Typography>
                                            <Typography variant={'h6'} fontWeight={700} sx={{my: 0.5, color: isPriceSelected ? 'primary.main' : 'inherit'}}>
                                                {formatMoney(d.actualValue)}
                                            </Typography>
                                            <Typography variant={'caption'} sx={{opacity: 0.8, display: 'block', mt: 1}}>
                                                Remove the formula and keep the current live price.
                                            </Typography>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Card>
                        );
                    })}
                </Box>
            </DialogContent>
            <DialogActions className={'d-flex align-items-center justify-content-between'} sx={{padding: 0}}>
                <Button
                    disabled={!open || loading}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={() => onClose(false)}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                <Button
                    disabled={!open || !allResolved || loading}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={handleSubmit}
                    borderRadius={'8px 0 8px 0'}
                >
                    {
                        loading ? 'Submitting...' : 'Submit Resolutions'
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FormulaDiscrepancyResolutionDialog;
